import { useMemo } from "react";

import { Select } from "../../../components/Select/Select";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import {
  noIntStatKey,
  schemaKeyOptionsWithUnset,
} from "../../../lib/schemaFields";
import styles from "./StatKeySelect.module.css";

interface Props {
  value: string;
  statsSchema: SchemaField[];
  onChange: (next: string) => void;
  /**
   * Say what an empty schema means, and disable the control while it is.
   *
   * Omit it where an unset key is a complete, working configuration
   * (`guild.succession_level_key`): the sentinel is then the honest answer and
   * there is nothing to warn about. Pass it where the field is one half of
   * something that needs both (`invites.trigger_stat_key`), because an enabled
   * list holding one row the operator cannot use explains nothing.
   */
  emptyLabel?: string;
  /**
   * How wide the trigger is, which belongs to the layout around it: a key
   * standing alone lines up with the key pair above it, one inside a threshold
   * grid fills its column.
   */
  className?: string;
  /** No visible <label> points at these — FieldRow renders none. */
  "aria-label": string;
}

/**
 * The picker for a single `int` field of `profile.stats_schema` — the control
 * `guild.succession_level_key` and `invites.trigger_stat_key` share.
 *
 * It was the succession row's, and the second caller is what moved it here:
 * the option list, the sentinel, the monospace value and the toned-down trigger
 * are one contract with the operator, and a second copy is how the two would
 * come to disagree about which fields are pickable. What the key MEANS stays
 * with each caller — that is the sentence under the box, and no two of them say
 * the same thing.
 */
export function StatKeySelect({
  value,
  statsSchema,
  onChange,
  emptyLabel,
  className = "",
  "aria-label": ariaLabel,
}: Props) {
  const empty = useMemo(
    () => emptyLabel !== undefined && noIntStatKey(statsSchema, value),
    [emptyLabel, statsSchema, value],
  );
  const options = useMemo(
    // Nothing to offer, so no list: an empty `options` leaves Select on its own
    // placeholder branch, which is where the empty label belongs.
    () => (empty ? [] : schemaKeyOptionsWithUnset(statsSchema, value)),
    [empty, statsSchema, value],
  );

  return (
    <Select
      className={`${styles.select} ${
        value === "" ? styles.unset : ""
      } ${className}`}
      value={value}
      onChange={onChange}
      options={options}
      monospaceValue={value !== ""}
      placeholder={empty ? emptyLabel : "— not set —"}
      disabled={empty}
      aria-label={ariaLabel}
    />
  );
}
